import * as THREE from 'three'

/**
 * Design tokens for the isometric technical-illustration look:
 * near-white shells, a single saturated accent, charcoal for "equipment",
 * and hairline ink contours on every silhouette edge.
 */
export const COLORS = {
  shell: '#ffffff',
  shellSoft: '#f3f4f5',
  panel: '#e8e9eb',
  surface: '#fcfcfd',
  floor: '#fafafa',
  wall: '#ffffff',
  accent: '#efc33c',
  accentDeep: '#dcae23',
  dark: '#2e2f31',
  darkSoft: '#404244',
  darkDeep: '#212224',
  metal: '#c2c6ca',
  metalDark: '#8e9296',
  glass: '#eef3f6',
  screen: '#f5f6f8',
  foliage: '#2e2f31',
  soil: '#4c4e50',
  wood: '#e9e2d6',
  ink: '#1b1c1e',
} as const

export interface ToneSpec {
  /** Token name resolved through the live palette (allows runtime accent swaps). */
  color: keyof typeof COLORS
  opacity?: number
  transparent?: boolean
  depthWrite?: boolean
  side?: THREE.Side
  /** Excluded from the merged contour pass when false (e.g. glass gets its own thin frame). */
  outline?: boolean
}

/**
 * Semantic surface vocabulary. Assets reference tones, never raw hex, so the
 * whole catalog restyles from one place.
 */
export const TONES = {
  shell: { color: 'shell' },
  shellSoft: { color: 'shellSoft' },
  surface: { color: 'surface' },
  panel: { color: 'panel' },
  floor: { color: 'floor' },
  wall: { color: 'wall' },
  accent: { color: 'accent' },
  accentDeep: { color: 'accentDeep' },
  dark: { color: 'dark' },
  darkSoft: { color: 'darkSoft' },
  darkDeep: { color: 'darkDeep' },
  metal: { color: 'metal' },
  metalDark: { color: 'metalDark' },
  screen: { color: 'screen' },
  // Leaves are flat shape geometry, so they must render from both sides.
  foliage: { color: 'foliage', side: THREE.DoubleSide },
  soil: { color: 'soil' },
  wood: { color: 'wood' },
  glass: {
    color: 'glass',
    opacity: 0.22,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  },
} as const satisfies Record<string, ToneSpec>

export type Tone = keyof typeof TONES

export type Palette = Record<keyof typeof COLORS, string>

export const DEFAULT_PALETTE: Palette = { ...COLORS }

export type RenderTheme = 'light' | 'dark'

/**
 * Night version of the illustration language — a re-authored palette, not an
 * inversion. Shells go to charcoal, the "equipment" ramp flips so chairs and
 * screens read as the bright mass instead of the dark one, and the accent is
 * untouched because saturated yellow carries over unchanged.
 */
export const DARK_COLORS: Palette = {
  shell: '#252a31',
  shellSoft: '#1f242a',
  surface: '#2a3037',
  panel: '#2f353d',
  floor: '#1b1f25',
  wall: '#1e232a',
  accent: '#efc33c',
  accentDeep: '#d6a92a',
  dark: '#c9cfd7',
  darkSoft: '#b3bac4',
  darkDeep: '#dce1e8',
  metal: '#6d757f',
  metalDark: '#565d67',
  glass: '#7e8a96',
  screen: '#323841',
  foliage: '#ccd3db',
  soil: '#3b424a',
  wood: '#4a4237',
  ink: '#e7ebf0',
}

export function paletteFor(theme: RenderTheme): Palette {
  return theme === 'dark' ? { ...DARK_COLORS } : { ...DEFAULT_PALETTE }
}

/** Page colour the render sits on, per theme. */
export const BACKGROUND: Record<RenderTheme, string> = {
  light: '#ffffff',
  dark: '#101317',
}

/** Floor grid, per theme. */
export const GRID_COLOR: Record<RenderTheme, string> = {
  light: '#dfe3e8',
  dark: '#2b313a',
}

/** Drop-shadow ink under the platform, per theme. */
export const SHADOW_COLOR: Record<RenderTheme, string> = {
  light: 'rgba(15, 18, 24, 0.20)',
  dark: 'rgba(0, 0, 0, 0.55)',
}

export interface ShadingRig {
  /**
   * Signed offsets in perceptual sRGB. Negative darkens towards black by a
   * proportion of the base; positive lightens towards white by a proportion of
   * the remaining headroom. One rig therefore separates the planes of a nearly
   * white surface AND of a nearly black one — a pure multiply only works for
   * the first, which is why dark mode needs its own numbers rather than an
   * inverted palette alone.
   */
  ambient: number
  keyDir: THREE.Vector3
  keyIntensity: number
  fillDir: THREE.Vector3
  fillIntensity: number
  /** Extra darkening on downward-facing planes so undersides do not float. */
  groundBias: number
}

const KEY_DIR = new THREE.Vector3(0.35, 0.9, 0.26).normalize()
const FILL_DIR = new THREE.Vector3(0.7557, 0.2519, -0.6045).normalize()

/**
 * Light rig, solved so a pure white box seen from the default isometric corner
 * lands on exactly the tones the reference illustrations use:
 * top +0.000 (#ffffff), left −0.075 (#ececec), right −0.122 (#e0e0e0).
 */
export const SHADING_LIGHT: ShadingRig = {
  ambient: -0.1136,
  keyDir: KEY_DIR,
  keyIntensity: 0.1486,
  fillDir: FILL_DIR,
  fillIntensity: -0.0799,
  groundBias: 0.06,
}

/**
 * Dark rig, solved for top +0.170, left +0.055, right 0.000: the planes are
 * separated by lifting the lit faces out of the charcoal instead of pushing the
 * shaded ones into it.
 */
export const SHADING_DARK: ShadingRig = {
  ambient: -0.0018,
  keyDir: KEY_DIR,
  keyIntensity: 0.2186,
  fillDir: FILL_DIR,
  fillIntensity: -0.0988,
  groundBias: 0.09,
}

export function shadingFor(theme: RenderTheme): ShadingRig {
  return theme === 'dark' ? SHADING_DARK : SHADING_LIGHT
}

/** Contour ink, per theme. */
export const INK: Record<RenderTheme, string> = {
  light: COLORS.ink,
  dark: DARK_COLORS.ink,
}

/** Contour line defaults, in CSS pixels. */
export const OUTLINE = {
  color: COLORS.ink,
  width: 1.4,
  /** Dihedral angle (deg) above which an edge is a crease and always drawn. */
  threshold: 22,
  /**
   * Nominal viewport height the line weight is authored against. The exporter
   * scales `width` by `renderSize / referenceHeight` so a 2048px PNG carries the
   * same line-weight-to-image ratio as the editor.
   */
  referenceHeight: 1000,
  selectedColor: '#1f6feb',
  selectedWidth: 2.4,
  ghostColor: '#7a869a',
}

/** One world unit is one metre. */
export const UNIT = 1

/** Placement grid, in metres. */
export const GRID_STEP = 0.25
